<script>
  import { push } from 'svelte-spa-router';
  import { authApi } from '../stores/api.js';
  import { isCustomer } from '../stores/roles.js';
  import { _ } from '../lib/i18n.js';
  import { ArrowLeft, Key, Check, AlertCircle, AlertTriangle, Loader } from 'lucide-svelte';
  import Layout from '../components/Layout.svelte';

  let currentPassword = '';
  let newPassword = '';
  let confirmPassword = '';
  let loading = false;
  let error = '';
  let success = false;
  /**
   * What the backend could not take away, when it could not.
   *
   * A password change revokes the account's other SuperTokens sessions and its
   * OAuth grants at Hydra, and either can fail without failing the change
   * itself — the password IS different by then. The response says so with a
   * `warnings` array and a sentence; throwing that away and painting the green
   * box told the user they were safe while a stolen refresh token, valid for
   * another 336 hours, was still driving /mcp. Only the backend log knew.
   */
  let warning = '';
  /** @type {string[]} */
  let warningCodes = [];

  /** One line per thing that is still live, so "contact an administrator" has a subject. */
  const WARNING_DETAIL = {
    sessions: 'Le altre sessioni aperte su altri dispositivi potrebbero restare attive.',
    oauth_grants: 'Le autorizzazioni concesse alle applicazioni collegate (assistenti AI e altri client) '
      + 'potrebbero restare valide.',
    revocation_pending: 'Le altre sessioni e le applicazioni collegate restano bloccate finché la '
      + 'disconnessione non è completata.'
  };

  function goBack() {
    if ($isCustomer) {
      push('/portal');
    } else {
      push('/');
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    error = '';
    success = false;
    warning = '';
    warningCodes = [];

    // Validations
    if (!currentPassword) {
      error = 'Inserisci la password attuale';
      return;
    }

    if (newPassword.length < 8) {
      error = 'La nuova password deve essere di almeno 8 caratteri';
      return;
    }

    if (newPassword !== confirmPassword) {
      error = 'Le password non coincidono';
      return;
    }

    try {
      loading = true;
      const response = await authApi.changePassword(currentPassword, newPassword);
      success = true;
      warningCodes = Array.isArray(response?.warnings) ? response.warnings : [];
      // The backend's own sentence, not a reworded one: it is the only place
      // that knows which half failed.
      if (warningCodes.length > 0) {
        warning = response?.message
          || 'Password aggiornata, ma non è stato possibile revocare tutte le sessioni e le '
            + 'autorizzazioni esistenti. Contatta un amministratore.';
      }
      currentPassword = '';
      newPassword = '';
      confirmPassword = '';
    } catch (err) {
      console.error('[ChangePassword] Error:', err);
      error = err.message || 'Errore durante il cambio password';
    } finally {
      loading = false;
    }
  }
</script>

{#if $isCustomer}
  <!-- Customer portal version -->
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        <button
          on:click={goBack}
          class="flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
        >
          <ArrowLeft size={20} class="mr-2" />
          <span>Torna al Portale</span>
        </button>
        <div class="flex items-center space-x-3">
          <img src="/webdeploy-icon.png" alt="WebDeploy" class="h-8 w-8" />
          <span class="font-semibold text-gray-900 dark:text-white">Portale Cliente</span>
        </div>
      </div>
    </header>

    <main class="max-w-md mx-auto px-4 py-8">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div class="flex items-center space-x-3 mb-6">
          <div class="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
            <Key size={24} class="text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 class="text-xl font-bold text-gray-900 dark:text-white">Cambia Password</h1>
            <p class="text-sm text-gray-500 dark:text-gray-400">Aggiorna la tua password di accesso</p>
          </div>
        </div>

        {#if success && warning}
          <!-- Amber, not green: the password changed, but something that had to
               be revoked was not, and the user is the only one who can ask for
               it to be checked. -->
          <div class="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <div class="flex items-start">
              <AlertTriangle size={20} class="text-amber-500 mr-2 shrink-0 mt-0.5" />
              <div class="text-amber-800 dark:text-amber-200 space-y-1">
                <p class="font-medium">{warning}</p>
                <ul class="list-disc list-inside text-sm">
                  {#each warningCodes as code}
                    <li>{WARNING_DETAIL[code] || code}</li>
                  {/each}
                </ul>
              </div>
            </div>
          </div>
        {:else if success}
          <div class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
            <div class="flex items-center">
              <Check size={20} class="text-green-500 mr-2" />
              <p class="text-green-700 dark:text-green-300">Password aggiornata con successo!</p>
            </div>
          </div>
        {/if}

        {#if error}
          <div class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div class="flex items-center">
              <AlertCircle size={20} class="text-red-500 mr-2" />
              <p class="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        {/if}

        <form on:submit|preventDefault={handleSubmit} class="space-y-4">
          <div>
            <label for="currentPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password Attuale *
            </label>
            <input
              id="currentPassword"
              type="password"
              bind:value={currentPassword}
              class="input w-full"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label for="newPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nuova Password *
            </label>
            <input
              id="newPassword"
              type="password"
              bind:value={newPassword}
              placeholder="Minimo 8 caratteri"
              class="input w-full"
              disabled={loading}
              required
              minlength="8"
            />
          </div>

          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Conferma Nuova Password *
            </label>
            <input
              id="confirmPassword"
              type="password"
              bind:value={confirmPassword}
              placeholder="Ripeti la nuova password"
              class="input w-full"
              disabled={loading}
              required
            />
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              on:click={goBack}
              class="btn btn-secondary"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={loading}
            >
              {#if loading}
                <Loader size={16} class="animate-spin mr-2" />
              {/if}
              Aggiorna Password
            </button>
          </div>
        </form>
      </div>
    </main>
  </div>
{:else}
  <!-- Internal user version with Layout -->
  <Layout>
    <div class="max-w-md mx-auto">
      <button
        on:click={goBack}
        class="mb-6 inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
      >
        <ArrowLeft size={20} class="mr-2" />
        <span class="font-medium">Torna alla Home</span>
      </button>

      <div class="card p-6">
        <div class="flex items-center space-x-3 mb-6">
          <div class="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
            <Key size={24} class="text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 class="text-xl font-bold text-gray-900 dark:text-white">Cambia Password</h1>
            <p class="text-sm text-gray-500 dark:text-gray-400">Aggiorna la tua password di accesso</p>
          </div>
        </div>

        {#if success && warning}
          <!-- Amber, not green: the password changed, but something that had to
               be revoked was not, and the user is the only one who can ask for
               it to be checked. -->
          <div class="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <div class="flex items-start">
              <AlertTriangle size={20} class="text-amber-500 mr-2 shrink-0 mt-0.5" />
              <div class="text-amber-800 dark:text-amber-200 space-y-1">
                <p class="font-medium">{warning}</p>
                <ul class="list-disc list-inside text-sm">
                  {#each warningCodes as code}
                    <li>{WARNING_DETAIL[code] || code}</li>
                  {/each}
                </ul>
              </div>
            </div>
          </div>
        {:else if success}
          <div class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
            <div class="flex items-center">
              <Check size={20} class="text-green-500 mr-2" />
              <p class="text-green-700 dark:text-green-300">Password aggiornata con successo!</p>
            </div>
          </div>
        {/if}

        {#if error}
          <div class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div class="flex items-center">
              <AlertCircle size={20} class="text-red-500 mr-2" />
              <p class="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        {/if}

        <form on:submit|preventDefault={handleSubmit} class="space-y-4">
          <div>
            <label for="currentPassword2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password Attuale *
            </label>
            <input
              id="currentPassword2"
              type="password"
              bind:value={currentPassword}
              class="input w-full"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label for="newPassword2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nuova Password *
            </label>
            <input
              id="newPassword2"
              type="password"
              bind:value={newPassword}
              placeholder="Minimo 8 caratteri"
              class="input w-full"
              disabled={loading}
              required
              minlength="8"
            />
          </div>

          <div>
            <label for="confirmPassword2" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Conferma Nuova Password *
            </label>
            <input
              id="confirmPassword2"
              type="password"
              bind:value={confirmPassword}
              placeholder="Ripeti la nuova password"
              class="input w-full"
              disabled={loading}
              required
            />
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              on:click={goBack}
              class="btn btn-secondary"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={loading}
            >
              {#if loading}
                <Loader size={16} class="animate-spin mr-2" />
              {/if}
              Aggiorna Password
            </button>
          </div>
        </form>
      </div>
    </div>
  </Layout>
{/if}
